import { FC, useState, useEffect } from "react";
import { Modal, Form, Button, ListGroup, Spinner, Nav, Badge, Dropdown } from "react-bootstrap";
import { PermissionService } from "@services/permissions/permissionService";
import type { Profile } from "@services/permissions/commands/cmdListProfiles";
import type { Role } from "@services/permissions/commands/cmdListRoles";

interface Props {
  show: boolean;
  onClose: () => void;
  onAdd: (profileId: number) => void;
  onCreate: (payload: {
    email: string;
    firstName: string;
    lastName: string;
    roleIds: number[];
    sendInvite: boolean;
  }) => Promise<boolean>;
  existingUserIds: number[];
  isSaving: boolean;
  allRoles: Role[];
}

type TabKey = "existing" | "new";

const permissionService = new PermissionService();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddUserModal: FC<Props> = ({ show, onClose, onAdd, onCreate, existingUserIds, isSaving, allRoles }) => {
  const [activeTab, setActiveTab] = useState<TabKey>("existing");
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [newEmail, setNewEmail] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newRoleIds, setNewRoleIds] = useState<number[]>([]);
  const [sendInvite, setSendInvite] = useState(true);

  useEffect(() => {
    if (show) {
      setActiveTab("existing");
      setSearch("");
      setNewEmail("");
      setNewFirstName("");
      setNewLastName("");
      setNewRoleIds([]);
      setSendInvite(true);
      loadProfiles();
    }
  }, [show]);

  async function loadProfiles() {
    setIsLoading(true);
    try {
      setProfiles(await permissionService.listProfiles());
    } catch {
      console.error("Failed to load profiles");
    } finally {
      setIsLoading(false);
    }
  }

  const existingSet = new Set(existingUserIds);
  const filtered = profiles
    .filter((p) => !existingSet.has(p.user_id))
    // A deactivated account cannot sign in, so offering it here only undoes an
    // offboarding — and the row it adds would render greyed out immediately.
    .filter((p) => p.is_active)
    .filter((p) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        p.email.toLowerCase().includes(q) ||
        p.first_name.toLowerCase().includes(q) ||
        p.last_name.toLowerCase().includes(q)
      );
    });

  const selectedRoles = allRoles.filter((r) => newRoleIds.includes(r.id));
  const availableRoles = allRoles.filter((r) => !newRoleIds.includes(r.id));

  const addRole = (roleId: number) => setNewRoleIds((prev) => [...prev, roleId]);
  const removeRole = (roleId: number) =>
    setNewRoleIds((prev) => prev.filter((id) => id !== roleId));

  const trimmedEmail = newEmail.trim();
  const trimmedFirst = newFirstName.trim();
  const trimmedLast = newLastName.trim();
  const emailValid = EMAIL_REGEX.test(trimmedEmail);
  const canCreate =
    emailValid && trimmedFirst.length > 0 && trimmedLast.length > 0 && newRoleIds.length > 0;

  const handleCreate = async () => {
    if (!canCreate) return;
    await onCreate({
      email: trimmedEmail,
      firstName: trimmedFirst,
      lastName: trimmedLast,
      roleIds: newRoleIds,
      sendInvite,
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Add User to Organization</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Nav
          variant="tabs"
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k as TabKey)}
          className="mb-3"
        >
          <Nav.Item>
            <Nav.Link eventKey="existing">Existing user</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="new">New user</Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === "existing" ? (
          <>
            <Form.Control
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-3"
              autoFocus
            />

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-muted text-center py-3 mb-0">
                {search ? "No matching users found." : "All users are already in this organization."}
              </p>
            ) : (
              <ListGroup style={{ maxHeight: 300, overflowY: "auto" }}>
                {filtered.map((profile) => (
                  <ListGroup.Item
                    key={profile.id}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="fw-medium small">
                        {profile.first_name} {profile.last_name}
                      </div>
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {profile.email}
                      </div>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => onAdd(profile.id)}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        <><i className="bi bi-plus me-1" />Add</>
                      )}
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}

            <p className="text-muted small mt-3 mb-0">
              Can't find them? Switch to <strong>New user</strong> to invite.
            </p>
          </>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="name@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                autoFocus
              />
            </Form.Group>

            <div className="d-flex gap-3 mb-3">
              <Form.Group className="flex-grow-1">
                <Form.Label className="small fw-semibold">First name</Form.Label>
                <Form.Control
                  type="text"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="flex-grow-1">
                <Form.Label className="small fw-semibold">Last name</Form.Label>
                <Form.Control
                  type="text"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Roles</Form.Label>
              <div className="border rounded p-2 d-flex flex-wrap align-items-center gap-2">
                {selectedRoles.length === 0 && (
                  <span className="text-muted small fst-italic px-1">No roles selected</span>
                )}
                {selectedRoles.map((role) => (
                  <Badge
                    key={role.id}
                    bg="light"
                    text="dark"
                    className="border px-2 py-1 d-flex align-items-center gap-2"
                  >
                    <span className="small fw-medium">{role.name}</span>
                    <button
                      type="button"
                      className="btn-close"
                      style={{ fontSize: "0.5rem" }}
                      onClick={() => removeRole(role.id)}
                      aria-label={`Remove ${role.name}`}
                    />
                  </Badge>
                ))}
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    size="sm"
                    disabled={availableRoles.length === 0}
                  >
                    <i className="bi bi-plus me-1" />Add role
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {availableRoles.map((role) => (
                      <Dropdown.Item key={role.id} onClick={() => addRole(role.id)}>
                        {role.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="send-invite"
              label="Send invitation email with sign-in link"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
            />
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Cancel</Button>
        {activeTab === "new" && (
          <Button variant="primary" onClick={handleCreate} disabled={!canCreate || isSaving}>
            {isSaving ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : (
              <i className="bi bi-check-circle me-2" />
            )}
            Create
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModal;
